'use client'

import useSWR from 'swr'
import {
  getAccessToken,
  getRefreshToken,
  getSecondsUntilExpiry,
  setTokens,
} from '../lib/cookieUtils'
import { refreshAccessToken } from '../lib/authApi'
import type { AuthTokens } from '../lib/cookieUtils'
import {
  BASE_BACKOFF_MS,
  MAX_REFRESH_RETRIES,
  REFRESH_THRESHOLD_SECONDS,
} from '../lib/constants'
import { useAuthProvider } from '../providers/AuthProvider'

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function refreshWithRetry(refreshToken: string): Promise<AuthTokens> {
  let lastError: Error | undefined

  for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
    try {
      return await refreshAccessToken(refreshToken)
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('Refresh failed')
      if (attempt < MAX_REFRESH_RETRIES - 1) {
        await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt))
      }
    }
  }

  throw lastError
}

export function useAuthSession() {
  const { status, logout } = useAuthProvider()

  useSWR<AuthTokens | null>(
    // Key is null when not authenticated — SWR disables the hook automatically.
    status === 'authenticated' ? 'auth/session' : null,
    async () => {
      const accessToken = getAccessToken()
      const secondsRemaining = accessToken
        ? getSecondsUntilExpiry(accessToken)
        : 0

      // Token is fresh — no network request needed. SWR will re-schedule via refreshInterval.
      if (secondsRemaining > REFRESH_THRESHOLD_SECONDS) {
        return null
      }

      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        await logout()
        return null
      }

      try {
        const tokens = await refreshWithRetry(refreshToken)
        setTokens(tokens)
        return tokens
      } catch {
        await logout()
        return null
      }
    },
    {
      // Dynamically recalculate the next refresh delay after every fetch.
      // Called with the latest return value of the fetcher after each successful run.
      refreshInterval() {
        const accessToken = getAccessToken()
        if (!accessToken) {
          return 0
        }
        const seconds = getSecondsUntilExpiry(accessToken)
        return Math.max(0, (seconds - REFRESH_THRESHOLD_SECONDS) * 1000)
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      // Retries are handled manually inside refreshWithRetry so SWR should not re-run on error.
      shouldRetryOnError: false,
    },
  )
}
